import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Badge,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function MissingSendDates() {
  const navigate = useNavigate();
  const [days, setDays] = useState('7');

  const parsedDays = Number(days);
  const withinDays = Number.isFinite(parsedDays) && parsedDays >= 0 ? parsedDays : 7;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'missing-send-dates', withinDays],
    queryFn: () => reportsApi.getMissingSendDates({ days: withinDays }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/missing-send-dates', { days: withinDays });
    } finally {
      setExporting(false);
    }
  };

  const rows = (data?.data || []).map((item) => [
    <Button key={item.rentalId} variant="plain" onClick={() => navigate(`/rentals/${item.rentalId}`)}>
      {item.orderNumber || item.rentalId.slice(0, 8)}
    </Button>,
    item.clinicName || 'Direct',
    item.customerName || '—',
    item.productName || '—',
    item.startDate ? new Date(item.startDate).toLocaleDateString() : '—',
    <Badge
      key={`days-${item.rentalId}`}
      tone={item.daysUntilStart < 0 ? 'critical' : item.daysUntilStart <= 1 ? 'warning' : 'info'}
    >
      {item.daysUntilStart < 0
        ? `${Math.abs(item.daysUntilStart)} days past`
        : `in ${item.daysUntilStart} days`}
    </Badge>,
    item.status,
  ]);

  return (
    <Page
      title="Missing Send Dates"
      subtitle="Orders at risk of missing dispatch — start date passed or imminent with no shipment"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Look ahead (days)"
              type="number"
              value={days}
              onChange={setDays}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Missing Send Dates report. Try refreshing.</p>
            </EmptyState>
          ) : !data || data.data.length === 0 ? (
            <EmptyState heading="No orders at risk" image="">
              <p>All upcoming rentals are dispatched or on track.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={2} gap="400">
                <StatCard label="Orders At Risk" value={String(data.summary.totalAtRisk)} />
                <StatCard label="Past Start Date" value={String(data.summary.pastStart)} />
              </InlineGrid>

              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Clinic', 'Customer', 'Product', 'Start Date', 'Urgency', 'Status']}
                rows={rows}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
